"use client";

import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  memo,
  ReactNode,
} from "react";

export type InputValue = string | number;

export interface FormInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  label?: string;
  name: string;
  value?: InputValue;
  onChange: (
    e: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  required?: boolean;
  className?: string;
  parentclassName?: string;


  clearIcon?: ReactNode;
  suffixIcon?: ReactNode;
  onClear?: () => void;


  action?: ReactNode;
  actionPosition?: "left" | "right";
}

const _FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      name,
      value,
      onChange,
      required = false,
      className = "",
      parentclassName = "",
      placeholder,
      type = "text",
      clearIcon,
      suffixIcon,
      onClear,
      action,
      actionPosition = "left",
      ...rest
    },
    ref
  ) => {
    return (
      <div className={`flex flex-col gap-2 ${parentclassName}`}>
        {label && (
          <label
            htmlFor={name}
            className="text-sm font-medium dark:text-gray-300"
          >
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}

        <div className={`relative`}>
          {/* input wrapper */}
          <div className="flex items-center h-12 bg-background/40 px-4 border border-[#262f41] rounded-lg gap-3">
            {suffixIcon && (
              <span className="flex items-center justify-center">
                {suffixIcon}
              </span>
            )}

            <input
              id={name}
              name={name}
              ref={ref}
              type={type}
              value={value ?? ""}
              onChange={onChange}
              placeholder={placeholder}
              required={required}
              className={`h-12 flex-1 bg-transparent px-1 dark:text-white focus:ring-2 focus:ring-foreground focus:outline-none my-no-focus
                ${action ? (actionPosition === "left" ? "pl-14" : "pr-14") : ""}
                ${className}
              `}
              {...rest}
            />

            {clearIcon && value && value !== "" && (
              <button
                type="button"
                onClick={onClear}
                className="flex items-center justify-center text-gray-400 hover:text-white transition"
              >
                {clearIcon}
              </button>
            )}
          </div>

          {/* 🔥 ABSOLUTE ACTION */}
          {action && (
            <div
              className={`absolute top-1/2 -translate-y-1/2
                ${actionPosition === "left" ? "left-2" : "right-2"}
              `}
            >
              {action}
            </div>
          )}
        </div>
      </div>
    );
  }
);

_FormInput.displayName = "FormInput";

const FormInput = memo(_FormInput);
export default FormInput;
